import date from 'date-and-time';
import { ReleaseListRow } from 'fym-shared/src/music24/releases';

/**
 * A song stays "in promotion" this many days after its last kit asset, reel or rollout post. The
 * same number as music24-api `ACTIVE_RELEASE_WINDOW_DAYS` (`src/kit/limits/activeReleases.ts`).
 */
export const ACTIVE_RELEASE_WINDOW_DAYS = 90;

export interface OtherActiveRelease {
  catalogItemUuid: string;
  title: string;
  /** The day the song leaves promotion: its last activity + 90 days. */
  activeUntil: Date;
}

/**
 * The other song that holds the one active release of Free (design catalog-add-song f3): a song
 * that is not `currentUuid`, with a made asset (not failed) or a post, and activity in the last
 * 90 days. The newest activity wins. Undefined when there is none.
 *
 * A hint only, from the Releases list the page already reads: the server rule
 * (`activeReleaseUuids`) stays the authority, and "Make the kit" still answers 402
 * `M24_LIMIT_RELEASES`.
 */
export function otherActiveRelease(
  rows: readonly ReleaseListRow[],
  titles: ReadonlyMap<string, string>,
  currentUuid: string,
  now: Date
): OtherActiveRelease | undefined {
  const since = date.addDays(now, -ACTIVE_RELEASE_WINDOW_DAYS, true).getTime();
  let best: { row: ReleaseListRow; at: number } | undefined;
  for (const row of rows) {
    if (row.catalogItemUuid === currentUuid || row.lastActivityAt === null) {
      continue;
    }
    const madeSomething = row.assets.count - row.assets.failedCount > 0 || row.posts.done + row.posts.planned > 0;
    const at = new Date(row.lastActivityAt).getTime();
    if (!madeSomething || Number.isNaN(at) || at < since || at > now.getTime()) {
      continue;
    }
    if (best === undefined || at > best.at) {
      best = { row, at };
    }
  }
  if (best === undefined) {
    return undefined;
  }
  const title = titles.get(best.row.catalogItemUuid);
  if (title === undefined) {
    return undefined;
  }
  return {
    catalogItemUuid: best.row.catalogItemUuid,
    title,
    // In UTC: a local add keeps the wall clock and moves the instant by an hour across a DST change.
    activeUntil: date.addDays(new Date(best.at), ACTIVE_RELEASE_WINDOW_DAYS, true),
  };
}

/** The page meter of the pre-save and SmartLink cards (design f3): "2 of 3 on Free". */
export interface PagesMeter {
  used: number;
  limit: number;
  isFull: boolean;
}

/** The meter, or undefined on a plan with no page limit or while the count loads. */
export function pagesMeterOf(limit: number | null, used: number | undefined): PagesMeter | undefined {
  if (limit === null || used === undefined) {
    return undefined;
  }
  return { used, limit, isFull: used >= limit };
}
